import { Role } from "@/interfaces/roles";
import {
  DeleteCode,
  PharmacistInterface,
  Setting,
  User,
  userDefault
} from "@/interfaces/user";
import { EventBus, store } from "@/internal";
import { messagesService } from "@/services/messages.service";
import { permissionRoleService } from "@/services/permissions.service";
import { pharmacistService } from "@/services/pharmacist.service";
import { traceabilityService } from "@/services/traceability.service";
import { usersService } from "@/services/user.service";
import { UserState } from "@/vuex/modules/user/user.types";
import { RootState } from "@/vuex/types";
import { TablePagination } from "helix-vue-components";
import cloneDeep from "lodash/cloneDeep";
import difference from "lodash/difference";
// @ts-ignore
import objectToFormData from "object-to-formdata";
import { ActionContext, ActionTree } from "vuex";

type UserActionContext = ActionContext<UserState, RootState>;
type UserActionTree = ActionTree<UserState, RootState>;

export const actions: UserActionTree = {
  async loadUsers(context: UserActionContext): Promise<any> {
    try {
      context.commit("setLoading", true);
      const payload: User[] = await usersService.get();
      const pagination: TablePagination = await usersService.getPagination();
      context.commit("setPagination", pagination);
      context.commit("setUsers", payload);
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      context.commit("setLoading", false);
    }
  },
  updateCurrentUser(context: UserActionContext, user: User) {
    context.commit("setCurrentUser", user);
  },
  async save(context: UserActionContext, user: any): Promise<any> {
    try {
      context.commit("setLoading", true);
      const roles = [...user.roles!];
      const settings: Setting = { ...user.settings! };
      let userSaved: User;
      const metrcConfig = user.metrc || null;
      const biotrackConfig = user.biotrack || null;
      const getterPharmacistInfo = context.getters.pharmacistInfo;
      let pharmacist: PharmacistInterface = cloneDeep(user.pharmacistData);
      pharmacist = setPharmacistLocations(pharmacist);

      const hasMetrcConfig = !!user.hasMetrcConfig;
      const hasBiotrackConfig = !!user.hasBiotrackConfig;
      if (user.id) {
        // check if user updated is the same as the user logged in
        const currentLogged = context.rootGetters["authModule/user"];
        if (currentLogged.id === user.id) {
          user.redirectTo = "user-management-list";
          store.dispatch("authModule/updateUser", user);
          return;
        }
        const formData: FormData = formParseUser(user);
        if (user.avatar && !user.avatar.name) {
          formData.set("avatar", user.avatar, "picture.png");
        } else if (user.avatar === null) {
          formData.set("avatar", DeleteCode.DELETE_CODE);
        }
        const pharmacistFormData: FormData = formParsePharmacist(pharmacist);
        formData.append("_method", "PUT");
        const putResponse = await usersService.put(user, formData);
        userSaved = putResponse.data;
        if (getterPharmacistInfo && getterPharmacistInfo !== null) {
          const pharmacistPutResponse = await pharmacistService.put(pharmacist);
          context.commit("setPharmacistInfo", pharmacistPutResponse);
        } else {
          const pharmacistPostResponse = await pharmacistService.post(
            pharmacistFormData
          );

          context.commit("setPharmacistInfo", pharmacistPostResponse);
        }
      } else {
        user.email = user.profile!.company_email_address;
        const formData: FormData = formParseUser(user);
        if (user.avatar && !user.avatar.name) {
          formData.set("avatar", user.avatar, "picture.png");
        }
        userSaved = await usersService.post(formData);
        pharmacist.user_id = userSaved.id;
        const pharmacistFormData: FormData = formParsePharmacist(pharmacist);
        const pharmacistPostResponse = await pharmacistService.post(
          pharmacistFormData
        );

        context.commit("setPharmacistInfo", pharmacistPostResponse);
      }
      await permissionRoleService.updateRolesByUser(
        userSaved.id!,
        roles as number[]
      );
      if (
        userSaved.settings!.current_location_id !==
          settings.current_location_id ||
        userSaved.settings!.locale !== settings.locale
      ) {
        await usersService.updateSettings(userSaved.id!, {
          current_account_id:
            settings.current_account_id ||
            userSaved.settings!.current_account_id!,
          current_location_id:
            settings.current_location_id ||
            userSaved.settings!.current_location_id,
          locale: settings.locale || userSaved.settings!.locale,
          preferences: userSaved.settings!.preferences
        });
      }
      if (userSaved && metrcConfig && !!+process.env.VUE_APP_METRC) {
        if (hasMetrcConfig) {
          if (
            !metrcConfig.user_key &&
            !metrcConfig.expiration &&
            !metrcConfig.occupational_license_number
          ) {
            traceabilityService.deleteUserTraceability(userSaved.id!);
          } else {
            traceabilityService.updateUserMetrcConfig(
              metrcConfig,
              userSaved.id!
            );
          }
        }
      }
      if (userSaved && biotrackConfig && hasBiotrackConfig) {
        if (!biotrackConfig.username && !biotrackConfig.password) {
          traceabilityService.deleteUserTraceability(userSaved.id!);
        } else {
          traceabilityService.updateUserBiotrackConfig(
            biotrackConfig,
            userSaved.id!
          );
        }
      }
      EventBus.$emit("notify", { text: "user_saved", color: "success" });
      await store.dispatch("AccountManagementModule/loadAvailability", {
        root: true
      });
      EventBus.$emit("route", {
        name: user.redirectTo || "user-management-list"
      });
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      context.commit("setLoading", false);
    }
  },

  async deleteUser(context: UserActionContext, user: User): Promise<any> {
    try {
      await usersService.deleteForce(user);
      EventBus.$emit("notify", { text: "user_deleted", color: "success" });
      context.dispatch("AccountManagementModule/loadAvailability", {
        root: true
      });
      context.dispatch("loadUsers");
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  },
  async searchUsers(context: UserActionContext, value: string): Promise<any> {
    try {
      context.commit("setLoading", true);
      const payload: User[] = await usersService.searchUsers(value);
      context.commit("setUsers", payload);
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      context.commit("setLoading", false);
    }
  },
  async findUser(context: UserActionContext, id: number): Promise<any> {
    try {
      context.commit("setLoading", true);
      const embed = "profile";
      const resp = await Promise.all([
        usersService.findById(Number(id), {
          embed
        }),
        permissionRoleService.rolesByUser(id)
      ]);
      const user = resp[0];
      user.roles = resp[1].map((role: Role) => role.id);
      user.settings!.current_account_id = +user.settings!.current_account_id!;
      user.settings!.current_location_id = +user.settings!.current_location_id!;
      context.commit("setCurrentUser", user);
    } catch (e) {
      messagesService.renderErrorMessage(e);
    } finally {
      context.commit("setLoading", false);
    }
  },
  async getPharmacistInfo(
    context: UserActionContext,
    userId: number
  ): Promise<any> {
    try {
      context.commit("setLoading", true);
      const payload: PharmacistInterface[] = await pharmacistService.getById(
        userId
      );
      context.commit("setPharmacistInfo", payload);
    } catch (e) {
      context.commit("setPharmacistInfo", null);
    } finally {
      context.commit("setLoading", false);
    }
  },
  async sort(context: UserActionContext, filters: object): Promise<any> {
    try {
      usersService.sortQuery(filters);
    } catch (e) {
      messagesService.renderErrorMessage(e);
    }
  },
  async resetForm(context: UserActionContext, filters: object): Promise<any> {
    context.commit("setCurrentUser", { ...userDefault });
  }
};

export function formParseUser(user: User) {
  return objectToFormData({
    ...user,
    settings: undefined,
    roles: undefined
  });
}

export function formParsePharmacist(pharmacist: PharmacistInterface) {
  return objectToFormData({
    ...pharmacist
  });
}

export function setPharmacistLocations(pharmacistData: PharmacistInterface) {
  let pharmacistLocationAdd = pharmacistData.pharmacist_location.add || [];
  let pharmacistLocationOld = pharmacistData.pharmacist_location.delete || [];
  let filteredLocationAdd: number[] = [];
  let filteredLocationOld: number[] = [];

  filteredLocationAdd = difference(
    pharmacistLocationAdd,
    pharmacistLocationOld
  );
  filteredLocationOld = difference(
    pharmacistLocationOld,
    pharmacistLocationAdd
  );
  pharmacistLocationAdd = filteredLocationAdd;
  pharmacistLocationOld = filteredLocationOld;
  if (pharmacistLocationAdd.length && pharmacistLocationOld.length) {
    pharmacistData.pharmacist_location = {
      add: pharmacistLocationAdd,
      delete: pharmacistLocationOld
    };
  } else if (
    pharmacistLocationAdd.length &&
    pharmacistLocationOld.length === 0
  ) {
    pharmacistData.pharmacist_location = {
      add: pharmacistLocationAdd
    };
  } else if (
    pharmacistLocationAdd.length === 0 &&
    pharmacistLocationOld.length
  ) {
    pharmacistData.pharmacist_location = {
      delete: pharmacistLocationOld
    };
  } else if (
    pharmacistLocationAdd.length === 0 &&
    pharmacistLocationOld.length === 0
  ) {
    pharmacistData.pharmacist_location = {};
  }
  return pharmacistData;
}
